import { useDebouncedCallback } from "@mantine/hooks";
import { useEffect, useState } from "react";
import type { UserSettingsPatch } from "../../../domain/index.ts";
import { getConfig } from "../config.ts";
import { useSaveUserSettingsMutation } from "../queries.ts";

const userSettingEvents = new Map([
	["change_color_blind_filter", "colorBlindFilter"],
	["change_countdown", "countdownLevel"],
	["change_font", "font"],
	["change_font_size", "fontSize"],
	["change_letter_spacing", "letterSpacing"],
	["change_line_height", "lineHeight"],
	["toggle_orientation", "orientation"],
	["change_read_stop", "readStopLevel"],
	["change_screen_mode", "screenModeLevel"],
	["change_subtitle", "subtitleLevel"],
	["change_subtitle_font_size", "subtitleFontSize"],
	["change_text_mode", "textModeLevel"],
	["change_word_spacing", "wordSpacing"],
	["change_zoom_level", "zoomLevel"],
]);

/**
 * Listens for settings changed messages from the Readiant iframe and saves user settings.
 * Messages are debounced to prevent excessive API calls during rapid settings changes.
 */
export function useIframeUserSettingsSync() {
	const [changedSettings, setChangedSettings] = useState<UserSettingsPatch>({});
	const mutation = useSaveUserSettingsMutation();

	const debouncedSaveUserSettings = useDebouncedCallback(() => {
		mutation.mutate({ userSettings: changedSettings });
		setChangedSettings({});
	}, 5000);

	useEffect(() => {
		const readiantIframeUrl = getConfig("readiantIframeUrl");
		const expectedOrigin = new URL(readiantIframeUrl).origin;

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== expectedOrigin) {
				return;
			}

			const { data } = event;

			if (typeof data !== "object" || data === null) {
				return;
			}

			if (userSettingEvents.has(data.type)) {
				const settingKey = userSettingEvents.get(data.type);

				if (settingKey)
					setChangedSettings((prevSettings) => ({
						...prevSettings,
						[settingKey]: data[settingKey] ?? null,
					}));
				debouncedSaveUserSettings();
			}
		};

		window.addEventListener("message", handleMessage);

		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, [debouncedSaveUserSettings]);
}
